import {useCallback, useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { applyBlockElements, debounce, generateFallbackConsent, waitForUC_UI } from "./consentUtils";

interface ConsentStatus {
    [service: string]: boolean | undefined;
}

declare global {
    interface Window {
        UC_UI?: {
            getServicesFullInfo: () => Promise<any>;
            getServicesConsentStatus: () => Promise<Record<string, boolean>>;
        };
        uc?: {
            blockElements: (elements: Record<string, string>) => void;
        };
    }
}


const useOverlayService = () => {
    const [services, setServices] = useState<{ name: string; selector: string; serviceId: string }[]>([]);
    const overlayStateRef = useRef<Record<string, boolean>>({});
    const iframeStateRef = useRef<Record<string, boolean>>({});
    const [isReady, setIsReady] = useState(false);
    const [pendingInitialConsent, setPendingInitialConsent] = useState<ConsentStatus | null>(null);
    const pathname = usePathname();

    const fetchServices = async () => {
        await waitForUC_UI();

        try {
            const servicesInfo = await window.UC_UI!.getServicesFullInfo();

            const detectedServices = servicesInfo.map((service: any) => ({
                name: service.name,
                selector: `iframe[data-service='${service.name}']`,
                serviceId: service.id,
            }));

            setServices(detectedServices);
            setIsReady(true);
        } catch (error) {
            console.error("Error fetching services:", error);
        }
    };

    const blockOverlayForService = useCallback(
        (service: { name: string; selector: string; serviceId: string }) => {
            applyBlockElements(service);
        },
        []
    );

    const unblockIframe = useCallback(
        (iframe: HTMLIFrameElement, serviceName: string) => {
            const dataSrc = iframe.getAttribute("data-src");
            if (dataSrc) {
                iframe.setAttribute("src", dataSrc);
                iframe.removeAttribute("data-src");
            }
            iframe.style.display = "block";
            iframeStateRef.current[serviceName] = true;
        },
        []
    );

    const handleOverlayService = useCallback(
        (serviceName: string, consentStatus: ConsentStatus) => {
            const service = services.find((s) => s.name === serviceName);
            if (!service) return;

            const currentConsent = consentStatus[serviceName];
            const iframes = document.querySelectorAll(service.selector);

            if (iframes.length === 0 && !overlayStateRef.current[service.name]) return;

            if (!currentConsent) {
                blockOverlayForService(service);
            } else {
                iframes.forEach((iframe) => unblockIframe(iframe as HTMLIFrameElement, serviceName));
                overlayStateRef.current[service.name] = false;
            }
        },
        [services, blockOverlayForService, unblockIframe]
    );

    const handleConsentStatusChange = useCallback(
        (consentStatus: ConsentStatus) => {
            services.forEach(({ name }) => {
                handleOverlayService(name, consentStatus);
            });
        },
        [services, handleOverlayService]
    );

    const handleInitialConsent = useCallback((initialConsent: ConsentStatus) => {
        if (!isReady) {
            setPendingInitialConsent(initialConsent);
            return;
        }
        services.forEach(({ name }) => {
            handleOverlayService(name, initialConsent);
        });
    }, [isReady, services, handleOverlayService]);

    /*
            Debounced handler for window resize events to manage iframe visibility.
            Necessary because iframes or overlays would otherwise become invisible after a resize beyond a breakpoint.
    */
    const debouncedResizeHandler = debounce(() => {
        services.forEach((service) => {
            const iframes = document.querySelectorAll(service.selector);
            iframes.forEach((iframe) => {
                if (iframeStateRef.current[service.name]) {
                    (iframe as HTMLIFrameElement).style.display = "block";
                    const dataSrc = iframe.getAttribute("data-src");
                    if (dataSrc && !iframe.getAttribute("src")) {
                        iframe.setAttribute("src", dataSrc);
                        iframe.removeAttribute("data-src");
                    }
                } else if (!overlayStateRef.current[service.name]) {
                    blockOverlayForService(service);
                }
            });
        });
    }, 200);

    useEffect(() => {
        const fetchAndSetServices = async () => {
            try {
                await fetchServices();
            } catch (error) {
                console.error("Error in fetchAndSetServices:", error);
            }
        };

        fetchAndSetServices().catch((error) =>
            console.error("Error in useEffect for fetchAndSetServices:", error)
        );
    }, []);

    useEffect(() => {
        const fetchInitialConsent = async (): Promise<ConsentStatus> => {
            if (services.length === 0) {
                return {};
            }

            if (typeof window.UC_UI?.getServicesConsentStatus === "function") {
                try {
                    return await window.UC_UI.getServicesConsentStatus();
                } catch (error) {
                    console.error("Error fetching initial consent from UC_UI:", error);
                }
            }

            return generateFallbackConsent(services);
        };

        if (services.length > 0) {
            fetchInitialConsent().then((consent) => {
                setPendingInitialConsent(consent);
            });
        }
    }, [services]);

    useEffect(() => {
        if (isReady && pendingInitialConsent) {
            handleInitialConsent(pendingInitialConsent);
            setPendingInitialConsent(null);
        }
    }, [isReady, pendingInitialConsent, handleInitialConsent]);

    useEffect(() => {
        const handleConsentEvent = (e: CustomEvent<ConsentStatus & { action?: string }>) => {
            if (e.detail?.action === "onInitialPageLoad") {
                handleInitialConsent(e.detail);
            } else {
                handleConsentStatusChange(e.detail);
            }
        };

        window.addEventListener("consents_initialized", handleConsentEvent as EventListener);
        window.addEventListener("consent_changed", handleConsentEvent as EventListener);
        window.addEventListener("resize", debouncedResizeHandler);

        return () => {
            window.removeEventListener("consents_initialized", handleConsentEvent as EventListener);
            window.removeEventListener("consent_changed", handleConsentEvent as EventListener);
            window.removeEventListener("resize", debouncedResizeHandler);
        };
    }, [services, debouncedResizeHandler, handleConsentStatusChange, handleInitialConsent]);

    useEffect(() => {
        debouncedResizeHandler();
    }, [pathname, debouncedResizeHandler]);

    return {};
};

export default useOverlayService;
